import { Button } from '../../components/Button';
import { BasketCard } from '../../components/BasketCard';
import { Divider } from '../../components/Divider';
import { Drawer } from '../../components/Drawer';
import { PriceDisplay } from '../../components/PriceDisplay';
import { ViewToggle } from '../../components/ViewToggle';
import type { ViewToggleOption } from '../../components/ViewToggle';
import { ItineraryView } from '../../composites/ItineraryView';
import { Recommendations } from '../../composites/Recommendations';
import { CalendarIcon } from '../../icons/CalendarIcon';
import { CheckIcon } from '../../icons/CheckIcon';
import { ListIcon } from '../../icons/ListIcon';
import { space } from '../../theme/tokens';
import {
  ActionBar,
  Body,
  ConfirmationCheck,
  ConfirmationRow,
  ItemsList,
  RecommendationsHeading,
  RecommendationsHeadingSecondary,
  TitleRow,
  TitleText,
  TotalRow,
  TotalLine,
  TotalLabel,
} from './BasketDrawerTemplate.styles';
import type {
  BasketDrawerLabels,
  BasketDrawerTemplateProps,
  BasketDrawerView,
} from './BasketDrawerTemplate.types';

const DEFAULT_LABELS: Required<BasketDrawerLabels> = {
  title: 'Your basket',
  closeLabel: 'Close basket',
  addedConfirmation: 'Added to basket',
  itemisedView: 'Itemised view',
  itineraryView: 'Itinerary view',
  total: 'Total',
  viewBasket: 'View Basket',
  checkout: 'Checkout',
  recommendationsHeadingPrimary: 'Wait.',
  recommendationsHeadingSecondary: "Don't forget to make plans...",
  selectGuestLabel: 'Select guest',
  noImageLabel: 'No image',
};

export function BasketDrawerTemplate({
  open,
  onClose,
  items,
  totalPrice,
  itemCount,
  view = 'itemised',
  onViewChange,
  guests,
  showAddedConfirmation = false,
  recommendations,
  onRecommendationSelect,
  onViewBasket,
  onCheckout,
  locale,
  labels,
}: BasketDrawerTemplateProps) {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const hasGuests = guests !== undefined && guests.length > 0;
  const canToggleView = Boolean(onViewChange && hasGuests);
  const showItinerary = view === 'itinerary' && hasGuests;
  const recs = recommendations ?? [];
  const showRecommendations = recs.length > 0;

  const toggleOptions: ViewToggleOption<BasketDrawerView>[] = [
    { value: 'itemised', label: resolvedLabels.itemisedView, Icon: ListIcon },
    { value: 'itinerary', label: resolvedLabels.itineraryView, Icon: CalendarIcon },
  ];

  const recommendationsTitle = (
    <RecommendationsHeading>
      {resolvedLabels.recommendationsHeadingPrimary}{' '}
      <RecommendationsHeadingSecondary>
        {resolvedLabels.recommendationsHeadingSecondary}
      </RecommendationsHeadingSecondary>
    </RecommendationsHeading>
  );

  const footer = (
    <ActionBar>
      <Button variant="secondary" fullWidth onClick={onViewBasket}>
        {`${resolvedLabels.viewBasket} (${itemCount})`}
      </Button>
      <Button variant="primary" fullWidth onClick={onCheckout}>
        {resolvedLabels.checkout}
      </Button>
    </ActionBar>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      aria-label={resolvedLabels.title}
      closeLabel={resolvedLabels.closeLabel}
      width="600px"
      mobileTopOffset={space[2]}
      backdropBlur
      footer={footer}
    >
      <Body data-testid="basket-drawer-template">
        <TitleRow>
          <TitleText>{resolvedLabels.title}</TitleText>
          {canToggleView && onViewChange && (
            <ViewToggle<BasketDrawerView>
              value={view}
              onChange={onViewChange}
              options={toggleOptions}
            />
          )}
        </TitleRow>

        {showAddedConfirmation && (
          <ConfirmationRow role="status">
            <ConfirmationCheck aria-hidden="true">
              <CheckIcon size={12} />
            </ConfirmationCheck>
            {resolvedLabels.addedConfirmation}
          </ConfirmationRow>
        )}

        {showItinerary && guests ? (
          <ItineraryView guests={guests} selectGuestLabel={resolvedLabels.selectGuestLabel} />
        ) : (
          <ItemsList>
            {items.map((item) => (
              <BasketCard
                key={item.id}
                {...item}
                locale={locale}
                noImageLabel={resolvedLabels.noImageLabel}
              />
            ))}
          </ItemsList>
        )}

        <TotalRow>
          <Divider />
          <TotalLine>
            <TotalLabel>{resolvedLabels.total}</TotalLabel>
            <PriceDisplay
              amount={totalPrice.amount}
              currencyCode={totalPrice.currencyCode}
              locale={locale}
              size="base"
              weight="medium"
            />
          </TotalLine>
        </TotalRow>

        {showRecommendations && (
          <Recommendations
            items={recs}
            onSelect={onRecommendationSelect}
            title={recommendationsTitle}
            noImageLabel={resolvedLabels.noImageLabel}
            layout="grid"
          />
        )}
      </Body>
    </Drawer>
  );
}
